import React from "react";
import { KeyTerm, BrainRegionCard, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const psychologyUnit2Notes: UnitNotes = {
  unitId: "unit-2",
  unitName: "Unit 2: Cognition",
  examWeight: "15–25%",
  focus: "How we select, organize, and interpret sensory information; think and solve problems; encode, store, and retrieve memories; and measure intelligence.",
  sections: [
    {
      id: "topic-2-1",
      title: "Topic 2.1: Perception",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Top-Down vs. Bottom-Up Processing</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">⬆️ Bottom-Up Processing</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Data-driven:</strong> Analysis that begins with the sensory receptors and works up to the brain's integration of sensory information.</p>
                <ImportantNote>
                  <strong>Example:</strong> You see a random shape on paper, your eyes detect lines and angles, and your brain pieces them together to realize it is a letter "A."
                </ImportantNote>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">⬇️ Top-Down Processing</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Concept-driven:</strong> Information processing guided by higher-level mental processes, drawing on our experience and expectations.</p>
                <ImportantNote>
                  <strong>Example:</strong> Reading a sentence with typos ("Yuo can raed tihs") because your brain expects words based on context.
                </ImportantNote>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Perceptual Organization (Gestalt Principles)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The Gestalt psychologists emphasized that <strong>"the whole is greater than the sum of its parts."</strong>
            </p>
            <div className="space-y-3">
              <KeyTerm 
                term="Figure-Ground" 
                definition="The organization of the visual field into objects (the figures) that stand out from their surroundings (the ground)."
              />
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800 mb-3">Grouping Principles</h4>
                <div className="space-y-2">
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Proximity</p>
                    <p className="text-xs text-slate-600">We group nearby figures together.</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Continuity</p>
                    <p className="text-xs text-slate-600">We perceive smooth, continuous patterns rather than discontinuous ones.</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Closure</p>
                    <p className="text-xs text-slate-600">We fill in gaps to create a complete, whole object.</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Similarity</p>
                    <p className="text-xs text-slate-600">We group similar figures together.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Perceptual Set & Schemas</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Perceptual Set" 
                definition="A mental predisposition to perceive one thing and not another (top-down). It is influenced by expectations, contexts, emotions, and motivation."
              />
              <ImportantNote>
                <strong>Example:</strong> If you expect a movie to be scary, you are more likely to interpret a shadow as a ghost.
              </ImportantNote>
              <KeyTerm 
                term="Schema" 
                definition="Concepts or mental frameworks that organize and interpret information."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">D. Attention</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Selective Attention" 
                definition="The focusing of conscious awareness on a particular stimulus."
              />
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🎤 Cocktail Party Effect</h4>
                <p className="text-sm text-slate-700 mt-2">The ability to attend to only one voice among many (but your name mentioned across the room will instantly grab your attention).</p>
              </div>
              <KeyTerm 
                term="Inattentional Blindness" 
                definition="Failing to see visible objects when our attention is directed elsewhere (e.g., the famous 'Gorilla Study')."
              />
              <KeyTerm 
                term="Change Blindness" 
                definition="Failing to notice changes in the environment."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">E. Depth Perception</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The ability to see objects in three dimensions although the images that strike the retina are two-dimensional; allows us to judge distance.
            </p>
            
            <h4 className="text-lg font-semibold text-slate-700 mb-2">Binocular Cues (Two eyes)</h4>
            <div className="space-y-3 mb-4">
              <KeyTerm 
                term="Retinal Disparity" 
                definition="By comparing images from the retinas in the two eyes, the brain computes distance—the greater the disparity (difference) between the two images, the closer the object."
              />
              <KeyTerm 
                term="Convergence" 
                definition="The extent to which the eyes converge inward when looking at an object (greater inward strain = closer object)."
              />
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Monocular Cues (One eye)</h4>
            <div className="grid md:grid-cols-2 gap-3">
              <KeyTerm term="Relative Size" definition="Smaller image is more distant." />
              <KeyTerm term="Interposition" definition="An object that blocks another is closer." />
              <KeyTerm term="Relative Clarity" definition="Hazy objects are farther away." />
              <KeyTerm term="Texture Gradient" definition="Indistinct texture signals distance." />
              <KeyTerm term="Relative Height" definition="Objects higher in our field of vision are perceived as farther away." />
              <KeyTerm term="Linear Perspective" definition="Parallel lines appear to converge with distance." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">F. Perceptual Constancy</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Perceiving objects as unchanging (having consistent shapes, size, lightness, and color) even as illumination and retinal images change.
            </p>
            <div className="space-y-3">
              <KeyTerm 
                term="Size Constancy" 
                definition="A car driving away looks smaller, but we know it isn't shrinking."
              />
              <KeyTerm 
                term="Color Constancy" 
                definition="An apple looks red in sunlight and shadow, despite changes in light wavelengths reflected."
              />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-2",
      title: "Topic 2.2: Thinking, Problem-Solving, Judgments, and Decision Making",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Concepts and Prototypes</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Concept" 
                definition="A mental grouping of similar objects, events, ideas, or people."
              />
              <KeyTerm 
                term="Prototype" 
                definition="A mental image or best example of a category."
              />
              <ImportantNote>
                <strong>Example:</strong> When asked to think of a "bird," a robin is a prototype; a penguin is not (though both are birds).
              </ImportantNote>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Problem Solving Strategies</h3>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">✅ Algorithm</h4>
                <p className="text-sm text-slate-700 mt-2">A methodical, logical rule or procedure that guarantees solving a particular problem.</p>
                <p className="text-xs text-green-700 mt-2"><strong>Example:</strong> Exploring every aisle to find hot sauce.</p>
                <p className="text-xs text-slate-600 mt-1 italic">Accurate but slow.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">⚡ Heuristic</h4>
                <p className="text-sm text-slate-700 mt-2">A simple thinking strategy/shortcut that often allows us to make judgments and solve problems efficiently.</p>
                <p className="text-xs text-blue-700 mt-2"><strong>Example:</strong> Going straight to the condiment aisle.</p>
                <p className="text-xs text-slate-600 mt-1 italic">Fast but error-prone.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">💡 Insight</h4>
                <p className="text-sm text-slate-700 mt-2">A sudden realization of a problem's solution (the "Aha!" moment). Associated with the right temporal lobe.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Obstacles to Problem Solving</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Confirmation Bias" 
                definition="A tendency to search for information that supports our preconceptions and to ignore or distort contradictory evidence."
              />
              <KeyTerm 
                term="Mental Set" 
                definition="A tendency to approach a problem in one particular way, often a way that has been successful in the past."
              />
              <KeyTerm 
                term="Functional Fixedness" 
                definition="The inability to see a new use for an object (e.g., not thinking to use a coin as a screwdriver)."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">D. Decision Making & Biases</h3>
            <div className="space-y-3">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Representativeness Heuristic</h4>
                <p className="text-sm text-slate-700 mt-2">Judging the likelihood of events based on how well they seem to represent, or match, particular prototypes (stereotypes).</p>
                <ImportantNote>
                  <strong>Example:</strong> Assuming a quiet, glasses-wearing man is a librarian rather than a farmer, ignoring the statistical fact that there are many more farmers.
                </ImportantNote>
              </div>
              <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                <h4 className="font-bold text-cyan-800">Availability Heuristic</h4>
                <p className="text-sm text-slate-700 mt-2">Estimating the likelihood of events based on their availability in memory; if instances come readily to mind (often due to vividness), we presume such events are common.</p>
                <ImportantNote>
                  <strong>Example:</strong> Fearing plane crashes more than car crashes because plane crashes make the news.
                </ImportantNote>
              </div>
              <KeyTerm 
                term="Overconfidence" 
                definition="The tendency to be more confident than correct."
              />
              <KeyTerm 
                term="Belief Perseverance" 
                definition="Clinging to one's initial conceptions after the basis on which they were formed has been discredited."
              />
              <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-800">Framing</h4>
                <p className="text-sm text-slate-700 mt-2">The way an issue is posed (framed) can significantly affect decisions and judgments.</p>
                <ImportantNote>
                  <strong>Example:</strong> "90% survival rate" sounds better than "10% mortality rate."
                </ImportantNote>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">E. Creativity</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🎯 Convergent Thinking</h4>
                <p className="text-sm text-slate-700 mt-2">Narrowing the available problem solutions to determine the single best solution (required for IQ tests).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🌈 Divergent Thinking</h4>
                <p className="text-sm text-slate-700 mt-2">Expanding the number of possible problem solutions; creative thinking that diverges in different directions.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-3",
      title: "Topic 2.3: Introduction to Memory",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Models of Memory</h3>
            
            <h4 className="text-lg font-semibold text-slate-700 mb-3">Information-Processing Model (Atkinson-Shiffrin)</h4>
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <span className="bg-blue-200 text-blue-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-blue-800">Sensory Memory</h4>
                  <p className="text-sm text-slate-600">Immediate, very brief recording of sensory info.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <span className="bg-purple-200 text-purple-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-purple-800">Short-Term Memory (STM)</h4>
                  <p className="text-sm text-slate-600">Activated memory that holds a few items briefly.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <span className="bg-green-200 text-green-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-green-800">Long-Term Memory (LTM)</h4>
                  <p className="text-sm text-slate-600">Relatively permanent and limitless storehouse of the memory system.</p>
                </div>
              </div>
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Working Memory Model (Baddeley)</h4>
            <KeyTerm 
              term="Working Memory" 
              definition="A newer understanding of short-term memory that focuses on conscious, active processing of incoming auditory and visual-spatial information, and of information retrieved from long-term memory. Includes the Central Executive."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Types of Memory</h3>
            
            <h4 className="text-lg font-semibold text-slate-700 mb-3">Explicit (Declarative) Memory</h4>
            <p className="text-sm text-slate-600 mb-3">Memory of facts and experiences that one can consciously know and "declare." (Processed in <strong>Hippocampus</strong>)</p>
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <KeyTerm 
                term="Episodic" 
                definition="Personal experiences/events."
              />
              <KeyTerm 
                term="Semantic" 
                definition="Facts and general knowledge."
              />
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Implicit (Non-declarative) Memory</h4>
            <p className="text-sm text-slate-600 mb-3">Retention independent of conscious recollection. (Processed in <strong>Cerebellum/Basal Ganglia</strong>)</p>
            <div className="grid md:grid-cols-2 gap-3">
              <KeyTerm 
                term="Procedural" 
                definition="Skills (riding a bike)."
              />
              <KeyTerm 
                term="Classically Conditioned" 
                definition="Associations (flinching at lightning)."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Biological Basis</h3>
            <KeyTerm 
              term="Long-Term Potentiation (LTP)" 
              definition="An increase in a synapse's firing potential after brief, rapid stimulation. Believed to be a neural basis for learning and memory."
            />
          </section>
        </div>
      )
    },
    {
      id: "topic-2-4",
      title: "Topic 2.4: Encoding Memories",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed italic">Getting information into the brain.</p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Processing Types</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🔄 Automatic Processing</h4>
                <p className="text-sm text-slate-700 mt-2">Unconscious encoding of incidental information (space, time, frequency).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">💪 Effortful Processing</h4>
                <p className="text-sm text-slate-700 mt-2">Encoding that requires attention and conscious effort.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Encoding Strategies</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Chunking" 
                definition="Organizing items into familiar, manageable units (e.g., phone numbers)."
              />
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Mnemonics</h4>
                <p className="text-sm text-slate-700 mt-2">Memory aids, especially those using vivid imagery and organizational devices.</p>
                <div className="mt-3 space-y-2">
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Method of Loci</p>
                    <p className="text-xs text-slate-600">Visualizing items in specific locations in a familiar room.</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Peg-Word System</p>
                    <p className="text-xs text-slate-600">Rhyming scheme (One is a bun, two is a shoe...).</p>
                  </div>
                </div>
              </div>
              <KeyTerm 
                term="Hierarchies" 
                definition="Dividing broad concepts into narrower concepts and facts."
              />
              <KeyTerm 
                term="Spacing Effect" 
                definition="The tendency for distributed study or practice to yield better long-term retention than is achieved through massed study (cramming)."
              />
              <KeyTerm 
                term="Testing Effect" 
                definition="Enhanced memory after retrieving, rather than simply rereading, information."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Levels of Processing</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Shallow Processing</h4>
                <p className="text-sm text-slate-700 mt-2">Encoding on a basic level based on the structure or appearance of words.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Deep Processing ⭐</h4>
                <p className="text-sm text-slate-700 mt-2">Encoding semantically, based on the meaning of the words; tends to yield the best retention.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-5",
      title: "Topic 2.5: Storing Memories",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed italic">Retaining information over time.</p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Capacity and Duration</h3>
            
            <h4 className="text-lg font-semibold text-slate-700 mb-3">Sensory Memory</h4>
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <KeyTerm 
                term="Iconic Memory" 
                definition="Visual stimuli (lasts < 1 sec)."
              />
              <KeyTerm 
                term="Echoic Memory" 
                definition="Auditory stimuli (lasts 3-4 sec)."
              />
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Short-Term/Working Memory</h4>
            <div className="space-y-3 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Capacity</h4>
                <p className="text-sm text-slate-700 mt-2">"Magical Number Seven, plus or minus two" (George Miller)</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Duration</h4>
                <p className="text-sm text-slate-700 mt-2">~20 seconds without rehearsal</p>
              </div>
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Long-Term Memory</h4>
            <KeyTerm 
              term="Capacity & Duration" 
              definition="Essentially unlimited capacity and duration."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Brain Structures</h3>
            <div className="space-y-3">
              <BrainRegionCard 
                name="Hippocampus" 
                functions="'Save button' for explicit memories."
              />
              <BrainRegionCard 
                name="Frontal Lobes" 
                functions="Working memory processing."
              />
              <BrainRegionCard 
                name="Cerebellum" 
                functions="Implicit memories created by classical conditioning."
              />
              <BrainRegionCard 
                name="Basal Ganglia" 
                functions="Procedural memories (motor movement)."
              />
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Amygdala</h4>
                <p className="text-sm text-slate-700 mt-2">Emotion-related memory formation.</p>
                <div className="bg-red-100 p-2 rounded mt-2">
                  <p className="text-xs text-red-800"><strong>Flashbulb Memories:</strong> Clear memory of an emotionally significant moment or event.</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-6",
      title: "Topic 2.6: Retrieving Memories",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed italic">Getting information out.</p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Measures of Retention</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Recall" 
                definition="Retrieving information not currently in conscious awareness (Fill-in-the-blank test)."
              />
              <KeyTerm 
                term="Recognition" 
                definition="Identifying items previously learned (Multiple-choice test)."
              />
              <KeyTerm 
                term="Relearning" 
                definition="Learning something more quickly when you learn it a second time."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Retrieval Cues</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Priming" 
                definition="The activation, often unconsciously, of particular associations in memory."
              />
              <KeyTerm 
                term="Context-Dependent Memory" 
                definition="Putting yourself back in the context where you experienced something can prime your memory retrieval (e.g., taking a test in the same seat you learned the info)."
              />
              <KeyTerm 
                term="State-Dependent Memory" 
                definition="What we learn in one state (drunk/sober) may be more easily recalled when we are again in that state."
              />
              <KeyTerm 
                term="Mood-Congruent Memory" 
                definition="The tendency to recall experiences that are consistent with one's current good or bad mood."
              />
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Serial Position Effect</h4>
                <p className="text-sm text-slate-700 mt-2">Our tendency to recall best the last and first items in a list.</p>
                <div className="mt-3 space-y-2">
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Recency Effect</p>
                    <p className="text-xs text-slate-600">Better recall of last items.</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Primacy Effect</p>
                    <p className="text-xs text-slate-600">Better recall of first items.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-7",
      title: "Topic 2.7: Forgetting and Other Memory Challenges",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Forgetting</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Anterograde Amnesia" 
                definition="Inability to form new memories (e.g., H.M. case study)."
              />
              <KeyTerm 
                term="Retrograde Amnesia" 
                definition="Inability to retrieve information from one's past."
              />
              <KeyTerm 
                term="Encoding Failure" 
                definition="Information never entered long-term memory (e.g., what a penny looks like exactly)."
              />
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Storage Decay (Ebbinghaus Forgetting Curve)</h4>
                <p className="text-sm text-slate-700 mt-2">The course of forgetting is initially rapid, then levels off with time.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Interference</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Proactive Interference</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Prior</strong> learning disrupts the recall of new information.</p>
                <p className="text-xs text-orange-700 mt-2">Example: Old password blocks new password.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Retroactive Interference</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>New</strong> learning disrupts the recall of old information.</p>
                <p className="text-xs text-blue-700 mt-2">Example: New song lyrics make you forget old ones.</p>
              </div>
            </div>
            <Mnemonic 
              title="POIN" 
              content="Proactive Old interferes; Retroactive New interferes"
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Motivated Forgetting</h3>
            <KeyTerm 
              term="Repression" 
              definition="In psychoanalytic theory (Freud), the basic defense mechanism that banishes anxiety-arousing thoughts, feelings, and memories from consciousness. (Current psychology largely rejects this concept)."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">D. Memory Construction Errors</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Misinformation Effect (Elizabeth Loftus)</h4>
                <p className="text-sm text-slate-700 mt-2">Incorporating misleading information into one's memory of an event.</p>
                <div className="bg-red-100 p-3 rounded mt-2">
                  <p className="text-xs text-red-800"><strong>Key Study:</strong> Participants watched a car crash. Those asked how fast cars were going when they <em>"smashed"</em> into each other reported higher speeds and broken glass compared to those asked when they <em>"hit"</em> each other.</p>
                </div>
              </div>
              <KeyTerm 
                term="Source Amnesia" 
                definition="Attributing to the wrong source an event we have experienced, heard about, read about, or imagined."
              />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-8",
      title: "Topic 2.8: Intelligence and Achievement",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Theories of Intelligence</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Charles Spearman - General Intelligence (g)</h4>
                <p className="text-sm text-slate-700 mt-2">Believed a single general intelligence factor underlies specific mental abilities and is therefore measured by every task on an intelligence test.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">L.L. Thurstone - Primary Mental Abilities</h4>
                <p className="text-sm text-slate-700 mt-2">Proposed 7 primary mental abilities (e.g., word fluency, spatial ability); opposed the idea of <em>g</em>.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Howard Gardner - Multiple Intelligences</h4>
                <p className="text-sm text-slate-700 mt-2">Viewed intelligence as multiple abilities that come in different packages.</p>
                <div className="mt-2 text-xs text-slate-600">
                  <p><strong>8 Types:</strong> Linguistic, Logical-Mathematical, Musical, Spatial, Bodily-Kinesthetic, Intrapersonal, Interpersonal, Naturalist</p>
                </div>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Robert Sternberg - Triarchic Theory</h4>
                <div className="mt-2 space-y-2">
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Analytical</p>
                    <p className="text-xs text-slate-600">Academic problem solving.</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Creative</p>
                    <p className="text-xs text-slate-600">Generating novel ideas.</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Practical</p>
                    <p className="text-xs text-slate-600">Street smarts/everyday tasks.</p>
                  </div>
                </div>
              </div>
              <KeyTerm 
                term="Emotional Intelligence" 
                definition="The ability to perceive, understand, manage, and use emotions."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Assessing Intelligence</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Alfred Binet" 
                definition="Designed the first test to predict school achievement in France. Concept of Mental Age."
              />
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Lewis Terman - Stanford-Binet</h4>
                <p className="text-sm text-slate-700 mt-2">Adapted Binet's test for US audiences.</p>
                <div className="bg-blue-100 p-2 rounded mt-2">
                  <p className="text-xs text-blue-800"><strong>IQ Formula (William Stern):</strong> (Mental Age / Chronological Age) × 100</p>
                </div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">David Wechsler - WAIS</h4>
                <p className="text-sm text-slate-700 mt-2">Created the most widely used intelligence test today (Wechsler Adult Intelligence Scale).</p>
                <p className="text-xs text-purple-700 mt-2">Contains verbal and performance (nonverbal) subtests.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Principles of Test Construction</h3>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Standardization</h4>
                <p className="text-sm text-slate-700 mt-2">Defining meaningful scores by comparison with the performance of a pretested group. Forms a <strong>Normal Curve</strong> (Bell Curve).</p>
                <div className="bg-green-100 p-2 rounded mt-2 space-y-1">
                  <p className="text-xs text-green-800"><strong>Mean:</strong> 100</p>
                  <p className="text-xs text-green-800"><strong>Standard Deviation:</strong> 15</p>
                  <p className="text-xs text-green-800"><strong>68%</strong> of people fall between 85-115</p>
                  <p className="text-xs text-green-800"><strong>95%</strong> fall between 70-130</p>
                </div>
              </div>
              <KeyTerm 
                term="Reliability" 
                definition="The extent to which a test yields consistent results (e.g., test-retest reliability)."
              />
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Validity</h4>
                <p className="text-sm text-slate-700 mt-2">The extent to which a test measures or predicts what it is supposed to.</p>
                <div className="mt-2 space-y-2">
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Content Validity</p>
                    <p className="text-xs text-slate-600">Did the test sample the behavior of interest? (e.g., Did the psych test ask about psychology?)</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Predictive Validity</p>
                    <p className="text-xs text-slate-600">Does the test predict future performance? (e.g., SAT predicting college GPA)</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">D. Dynamics of Intelligence</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Crystallized Intelligence</h4>
                <p className="text-sm text-slate-700 mt-2">Accumulated knowledge and verbal skills; tends to <strong>increase</strong> with age.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Fluid Intelligence</h4>
                <p className="text-sm text-slate-700 mt-2">Ability to reason speedily and abstractly; tends to <strong>decrease</strong> during late adulthood.</p>
              </div>
            </div>
            <div className="space-y-3 mt-4">
              <KeyTerm 
                term="Flynn Effect" 
                definition="The worldwide phenomenon that intelligence test performance has been improving over the decades."
              />
              <KeyTerm 
                term="Stereotype Threat" 
                definition="A self-confirming concern that one will be evaluated based on a negative stereotype, leading to decreased performance."
              />
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default psychologyUnit2Notes;
